import React from "react";
import { AppHeader } from "../components/CommunitySelection";
import { useLang } from "../lib/i18n";
import { Home, Wheat, Scale, HeartPulse, ArrowUpRight, ExternalLink, Users2, GraduationCap, Mail } from "lucide-react";

// Four structural pathways from the "Policy Pathways: From Empathy to Action" brief.
const PATHWAYS = [
  {
    icon: Home,
    n: "01",
    area: "Housing Stability & Eviction Prevention",
    hook: "When safe, affordable shelter consumes more than 50% of household income, an unexpected car repair or reduction in hours turns into a housing emergency.",
    reality: "Across the United States, there is a shortage of over 7 million affordable and available rental homes for extremely low-income households. In most counties, a full-time minimum-wage worker cannot afford a modest one-bedroom apartment.",
    lever: "Expanding tenant right-to-counsel protections, modernizing local zoning laws, and funding the Low-Income Housing Tax Credit (LIHTC) to expand permanently affordable housing supply.",
    resources: [
      { name: "Princeton Eviction Lab — National Eviction Map & State Fact Sheets", note: "Track local eviction filing rates and state-by-state tenant protections.", url: "https://evictionlab.org/" },
      { name: "National Low Income Housing Coalition — Out of Reach", note: "Search the exact hourly wage required to rent modest housing in your ZIP code.", url: "https://nlihc.org/oor" },
    ],
  },
  {
    icon: Wheat,
    n: "02",
    area: "Food Security & The Benefit Cliff",
    hook: "Working parents often face the impossible paradox of turning down an hourly raise because an extra dollar an hour can disqualify their family from thousands in vital food or childcare assistance.",
    reality: "Anti-poverty programs are frequently designed with abrupt phase-outs (\u201Cbenefit cliffs\u201D) rather than graduated step-downs, trapping families in place as their costs outpace wage gains.",
    lever: "Modernizing the federal Farm Bill to align SNAP benefits with real food costs, and creating gradual taper rates so working families never lose more in benefits than they gain in earnings.",
    resources: [
      { name: "Center on Budget and Policy Priorities — Research & Policy Analyses", note: "In-depth, nonpartisan analysis on safety-net adequacy and benefit-cliff solutions.", url: "https://www.cbpp.org/" },
      { name: "Bread for the World — Hunger & Legislative Policy Briefs", note: "Action toolkits connecting food security to federal legislative policy.", url: "https://www.bread.org/" },
    ],
  },
  {
    icon: Scale,
    n: "03",
    area: "Fair Wages & Economic Self-Sufficiency",
    hook: "A job is no longer a guaranteed exit from poverty when entry-level wages fall short of the baseline cost of living.",
    reality: "In the majority of U.S. markets, basic subsistence expenses — transportation, child care, healthcare premiums, and utilities — outpace baseline wages, leaving zero margin for emergency savings.",
    lever: "Establishing living wages indexed to local cost of living, expanding refundable child and earned income tax credits, and passing universal paid family and medical leave.",
    resources: [
      { name: "MIT Living Wage Calculator", note: "Look up the exact wage required to meet basic family needs in your county or metro area.", url: "https://livingwage.mit.edu/" },
    ],
  },
  {
    icon: HeartPulse,
    n: "04",
    area: "Healthcare Burden & Uncompensated Medical Debt",
    hook: "A single minor injury or deferred preventive check-up can wipe out months of financial progress and push a household into systemic collection traps.",
    reality: "Millions of working adults fall into the \u201Ccoverage gap\u201D — earning too much to qualify for Medicaid in non-expansion states, yet not enough to afford commercial premiums and deductibles.",
    lever: "Closing state-level Medicaid coverage gaps, regulating uncompensated hospital medical debt, and ensuring preventive primary care access for all community members.",
    resources: [
      { name: "KFF (Kaiser Family Foundation) — State Health Facts & Policy Hub", note: "Interactive maps covering health coverage, uninsured rates, and Medicaid status by state.", url: "https://www.kff.org/statedata/" },
    ],
  },
];

// Editable partner slots. Add or replace organizations here to feature them.
const PARTNERS = [
  {
    name: "Bread for the World",
    blurb: "Connect directly with grassroots policy networks to join state advocacy teams and engage lawmakers on hunger and nutrition.",
    cta: "Join an advocacy team",
    url: "https://www.bread.org/take-action/",
  },
];

const PathwayCard = ({ p }) => {
  const { t } = useLang();
  const Icon = p.icon;
  return (
    <div data-testid="pathway-card" className="bg-white rounded-lg border border-[var(--line)] p-6 sm:p-7">
      <div className="flex items-start gap-4">
        <span className="inline-flex items-center justify-center h-11 w-11 rounded-full bg-vin/10 text-vin shrink-0">
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-vin/50">{p.n}</div>
          <h3 className="font-display text-lg sm:text-xl font-semibold text-ink leading-snug">{t(p.area)}</h3>
          <p className="mt-3 text-sm text-ink-soft leading-relaxed italic">{t(p.hook)}</p>

          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-vin/70">{t("The systemic reality")}</div>
            <p className="mt-1 text-sm text-ink leading-relaxed">{t(p.reality)}</p>
          </div>
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-[#365C4C]">{t("Structural lever")}</div>
            <p className="mt-1 text-sm text-ink leading-relaxed">{t(p.lever)}</p>
          </div>

          <div className="mt-5">
            <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-vin/70 mb-2">{t("Explore the data & evidence")}</div>
            <ul className="space-y-2">
              {p.resources.map((r) => (
                <li key={r.name}>
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pathway-resource-link"
                    className="group inline-flex items-start gap-2 text-sm font-semibold text-vin hover:underline underline-offset-2"
                  >
                    <ExternalLink className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.75} />
                    <span>
                      {r.name}
                      <span className="block font-normal text-ink-soft not-italic">{t(r.note)}</span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function PolicyPathways() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-[var(--page)]">
      <AppHeader subtitle={t("Policy Pathways")} />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
        <div className="animate-rise">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-vin/70 mb-3">
            {t("From empathy to action")}
          </p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {t("Policy Pathways")}
          </h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            {t("What you just navigated in this simulation is not a matter of poor budgeting or bad luck — it is the direct consequence of systemic design. True change requires moving beyond treating individual symptoms to addressing the structural root causes.")}
          </p>
        </div>

        <div className="mt-8 space-y-4">
          {PATHWAYS.map((p) => (
            <PathwayCard key={p.area} p={p} />
          ))}
        </div>

        {/* Take collective action */}
        <div className="mt-10">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-ink leading-tight">
            {t("Take action in your community")}
          </h2>

          <div className="mt-5 grid gap-4">
            <div className="bg-vin text-white rounded-lg p-6 sm:p-7">
              <div className="flex items-center gap-2 text-white/80 mb-4">
                <Users2 className="w-4 h-4" strokeWidth={1.75} />
                <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">{t("Advocate with local & national coalitions")}</span>
              </div>
              <div className="space-y-4">
                {PARTNERS.map((partner) => (
                  <div key={partner.name} data-testid="partner-slot" className="rounded-lg bg-white/10 p-5">
                    <div className="font-display text-lg font-semibold">{partner.name}</div>
                    <p className="mt-1 text-sm text-white/85 leading-relaxed">{t(partner.blurb)}</p>
                    <a
                      href={partner.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="partner-cta-link"
                      className="mt-3 inline-flex items-center gap-2 bg-white text-vin font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-white/90 transition-colors duration-200"
                    >
                      {t(partner.cta)} <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
                    </a>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg border border-[var(--line)] p-6 sm:p-7">
              <div className="flex items-center gap-2 text-vin mb-3">
                <GraduationCap className="w-4 h-4" strokeWidth={1.75} />
                <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">{t("Bring this tool to your organization")}</span>
              </div>
              <p className="text-sm text-ink-soft leading-relaxed">
                {t("Use the Root Causes Challenge in classrooms, civic workshops, faith communities, and board orientations. Contact Matambanadzo Studio for facilitation toolkits and partnership inquiries.")}
              </p>
              <a
                href="mailto:pam@matambanadzostudio.com"
                data-testid="pathways-contact-link"
                className="mt-4 inline-flex items-center gap-2 bg-vin text-white font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-vin-hover transition-colors duration-200"
              >
                <Mail className="w-4 h-4" strokeWidth={1.75} /> {t("Inquiries & Partnerships")}
              </a>
              <p className="mt-2 text-xs text-ink-soft">pam@matambanadzostudio.com</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
